import type { Reference as ReferenceType } from '@/lib/zybooks-api';
import type { Editor } from '@tiptap/core';
import { createStore, useStore, type StoreApi } from 'zustand';

export type ReferenceStorage = {
    referencesByKey: Record<string, Record<string, ReferenceType>>;
    pendingReferencesByKey: Record<string, Record<string, true>>;
    referencedNodesById: Record<string, { isReferenced: true; inboundReferences: ReferenceType[] }>;
    tocSectionTitleByMaDocumentId: Record<string, string>;
    highlightedReferenceNodeId: string | null;
};

export function createDefaultReferenceStorage(): ReferenceStorage {
    return {
        referencesByKey: {},
        pendingReferencesByKey: {},
        referencedNodesById: {},
        tocSectionTitleByMaDocumentId: {},
        highlightedReferenceNodeId: null,
    };
}

const editorStores = new Map<Editor, StoreApi<ReferenceStorage>>();

export function getOrCreateReferenceStore(editor: Editor): StoreApi<ReferenceStorage> {
    let store = editorStores.get(editor);
    if (!store) {
        store = createStore<ReferenceStorage>(() => createDefaultReferenceStorage());
        editorStores.set(editor, store);
    }
    return store;
}

export function destroyReferenceStore(editor: Editor): void {
    editorStores.delete(editor);
}

/** Synchronous read of the current state — safe to call outside React. */
export function getReferenceStorage(editor: Editor): ReferenceStorage {
    return getOrCreateReferenceStore(editor).getState();
}

/** React hook — subscribes the calling component to reference storage changes. */
export function useReferenceStore<T>(editor: Editor, selector: (state: ReferenceStorage) => T): T {
    return useStore(getOrCreateReferenceStore(editor), selector);
}
